export const WIZARD_MODES = {
  CREATE: "create",
  EDIT: "edit",
  REACTIVATE: "reactivate",
  VIEW: "view"
};

export const DEFAULT_TTL_HOURS = "24";
export const DEFAULT_MEMORY = "4";
export const LATEST_TAG = "latest";
export const RELEASE_NAME_MAX_LENGTH = 32;
export const RELEASE_NAME_REGEX = /^[a-z]([-a-z0-9]*[a-z0-9])?(\.[a-z0-9]([-a-z0-9]*[a-z0-9])?)*$/;
export const SHA_REGEX = /^[0-9a-f]{40}$/;
export const UUID_V4_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export const SERVICE_REQUIREMENTS = {
  REQUIRED: "required",
  OPTIONAL: "optional"
};

export const NLU_MODES = {
  DEDICATED: "dedicated",
  API: "api"
};

export const VALID_CONFIG_ID_VALUES = {
  ACTIVE: "active",
  DRAFT: "draft"
};

export const FIELD_IDS = {
  RELEASE_NAME: "releaseName",
  CONFIG_ID: "configId",
  MEMORY: "memory",
  ASSISTANT_ID: "assistantId",
  NLU_RUNTIME_TAG: "nluRuntimeTag",
  NLU_MODE: "nluMode",
  USE_CASE: "useCase"
};

export const BUTTON_TEXTS = {
  NEXT: "Next",
  BACK: "Back",
  CANCEL: "Cancel",
  CLOSE: "Close",
  DEPLOY: "Deploy",
  SAVE: "Save",
  REACTIVATE: "Reactivate"
};

export const SERVICE_FIELDS = {
  dashboardClientTag: {
    id: "dashboardClientTag",
    label: "Dashboard Client",
    description: "Frontend for the dashboard",
    inputType: "sha"
  },
  dashboardServerTag: {
    id: "dashboardServerTag",
    label: "Dashboard Server",
    description: "Backend for the dashboard",
    inputType: "sha"
  },
  configurationServiceTag: {
    id: "configurationServiceTag",
    label: "Configuration Service",
    description: "Manages agent configs",
    inputType: "sha"
  },
  dataServiceTag: {
    id: "dataServiceTag",
    label: "Data Service",
    description: "Analytics & metrics backend",
    inputType: "sha"
  },
  widgetClientTag: {
    id: "widgetClientTag",
    label: "Widget Client",
    description: "Frontend chat widget",
    inputType: "sha"
  },
  widgetServerTag: {
    id: "widgetServerTag",
    label: "Widget Server",
    description: "Backend for both voice and chat agents",
    inputType: "sha"
  },
  voiceGatewayTag: {
    id: "voiceGatewayTag",
    label: "Voice Gateway",
    description: "Call audio for voice agents: Twilio media, speech-to-text and text-to-speech",
    inputType: "sha"
  },
  nluRuntimeTag: {
    id: "nluRuntimeTag",
    label: "NLU Runtime (API mode)",
    description: "Used by the dashboard for building/testing agents",
    inputType: "sha"
  },
  nluRuntimeDedicated: {
    id: "nluRuntimeTag",
    label: "NLU Runtime (Dedicated mode)",
    description: "Used to talk with a live agent",
    inputType: "sha",
    extraFields: ["assistantId", "configId", "memory", "useDraftConfiguration"]
  }
};

export const EXTRA_FIELDS = {
  assistantId: {
    id: "assistantId",
    label: "Assistant ID",
    inputType: "text"
  },
  configId: {
    id: "configId",
    label: "Config ID",
    inputType: "text"
  },
  useDraftConfiguration: {
    id: "useDraftConfiguration",
    label: "Is draft config",
    inputType: "switch",
    tooltip: "When disabled, connectors will be resolved from the active config"
  },
  memory: {
    id: "memory",
    label: "Memory (Gi)",
    inputType: "number"
  },
  deployDashboardInternal: {
    id: "deployDashboardInternal",
    label: "Deploy Dashboard Internal",
    inputType: "switch",
    subtitle: "Toggle to deploy the dashboard internal with the same commit of dashboard server"
  },
  deployKnowledgeService: {
    id: "deployKnowledgeService",
    label: "Deploy Knowledge-Service",
    inputType: "switch",
    subtitle: "Toggle to deploy the knowledge-service with the same commit of NLU Runtime (latest if no NLU commit is set)"
  },
  splitDraftPreview: {
    id: "splitDraftPreview",
    label: "Split draft & preview",
    inputType: "switch",
    tooltip: "Deploys two widget servers: one serving draft agents and one serving preview agents, so the dashboard talks to the right one per mode"
  }
};

export const SPLIT_DRAFT_PREVIEW_HINT = "Deploys two widget servers, one for draft agents and one for preview agents";
export const NLU_BUILD_WIDGET_SERVER_HINT = "Add a widget-server commit to test widget-server changes; it deploys draft and preview instances";

// How a use case decides to deploy draft + preview widget servers without showing the toggle
export const SPLIT_DRAFT_PREVIEW_MODES = {
  ALWAYS: "always",
  WITH_WIDGET_SERVER: "withWidgetServer"
};

export const USE_CASES = {
  UC1: {
    id: "UC1",
    label: "Dashboard UI Only",
    description: "Test dashboard-client changes",
    services: {
      dashboardClientTag: { requirement: SERVICE_REQUIREMENTS.REQUIRED, showLatestToggle: false }
    },
    nluMode: null,
    extraFields: [],
    limitations: [],
  },
  UC2: {
    id: "UC2",
    label: "Dashboard E2E",
    description: "Test Dashboard e2e, with optional CS, WS and NLU",
    services: {
      dashboardClientTag: { requirement: SERVICE_REQUIREMENTS.REQUIRED },
      dashboardServerTag: { requirement: SERVICE_REQUIREMENTS.REQUIRED },
      configurationServiceTag: { requirement: SERVICE_REQUIREMENTS.OPTIONAL },
      dataServiceTag: { requirement: SERVICE_REQUIREMENTS.OPTIONAL },
      widgetServerTag: { requirement: SERVICE_REQUIREMENTS.OPTIONAL, subtitle: SPLIT_DRAFT_PREVIEW_HINT },
      nluRuntimeTag: { requirement: SERVICE_REQUIREMENTS.OPTIONAL }
    },
    nluMode: NLU_MODES.API,
    // always deploy draft + preview widget servers for e2e, no toggle in the UI
    splitDraftPreview: SPLIT_DRAFT_PREVIEW_MODES.ALWAYS,
    extraFields: ["deployDashboardInternal", "deployKnowledgeService"],
    limitations: []
  },
  UC3: {
    id: "UC3",
    label: "NLU Changes - Build & Test",
    description: "Test nlu-runtime changes by creating a draft and building it",
    services: {
      nluRuntimeTag: { requirement: SERVICE_REQUIREMENTS.REQUIRED },
      dashboardClientTag: { requirement: SERVICE_REQUIREMENTS.REQUIRED },
      dashboardServerTag: { requirement: SERVICE_REQUIREMENTS.REQUIRED },
      configurationServiceTag: { requirement: SERVICE_REQUIREMENTS.OPTIONAL },
      widgetServerTag: { requirement: SERVICE_REQUIREMENTS.OPTIONAL, showLatestToggle: false, subtitle: NLU_BUILD_WIDGET_SERVER_HINT },
      widgetClientTag: { requirement: SERVICE_REQUIREMENTS.OPTIONAL, showLatestToggle: false, subtitle: "Add widget-client commit if you would like to test new client features" }
    },
    nluMode: NLU_MODES.API,
    // draft + preview widget servers only when a widget-server commit is given, no toggle in the UI
    splitDraftPreview: SPLIT_DRAFT_PREVIEW_MODES.WITH_WIDGET_SERVER,
    extraFields: ["deployKnowledgeService"],
    limitations: []
  },
  UC4: {
    id: "UC4",
    label: "Talk to an Agent",
    description: "Test a specific agent config Voice or Chat",
    services: {
      nluRuntimeDedicated: { requirement: SERVICE_REQUIREMENTS.REQUIRED },
      widgetServerTag: { requirement: SERVICE_REQUIREMENTS.REQUIRED },
      voiceGatewayTag: { requirement: SERVICE_REQUIREMENTS.OPTIONAL },
      widgetClientTag: { requirement: SERVICE_REQUIREMENTS.OPTIONAL }
    },
    nluMode: NLU_MODES.DEDICATED,
    extraFields: [],
    limitations: []
  },
  UC5: {
    id: "UC5",
    label: "Widget UI Only",
    description: "Test widget-client changes",
    services: {
      widgetClientTag: { requirement: SERVICE_REQUIREMENTS.REQUIRED, showLatestToggle: false }
    },
    nluMode: null,
    extraFields: [],
    limitations: []
  },
  ADVANCED: {
    id: "ADVANCED",
    label: "Advanced",
    description: "Full control — all services, your responsibility",
    services: {
      dashboardClientTag: { requirement: SERVICE_REQUIREMENTS.OPTIONAL },
      dashboardServerTag: { requirement: SERVICE_REQUIREMENTS.OPTIONAL },
      configurationServiceTag: { requirement: SERVICE_REQUIREMENTS.OPTIONAL },
      dataServiceTag: { requirement: SERVICE_REQUIREMENTS.OPTIONAL },
      widgetClientTag: { requirement: SERVICE_REQUIREMENTS.OPTIONAL },
      widgetServerTag: { requirement: SERVICE_REQUIREMENTS.OPTIONAL, extraFields: ["splitDraftPreview"] },
      voiceGatewayTag: { requirement: SERVICE_REQUIREMENTS.OPTIONAL },
      nluRuntimeTag: { requirement: SERVICE_REQUIREMENTS.OPTIONAL, modeSelector: true }
    },
    // Must match the mode selector's visual default (ServiceField falls back to API) —
    // otherwise an untouched selector sends no nluRuntimeModes and the NLU tag is silently ignored
    nluMode: NLU_MODES.API,
    extraFields: [
      "deployDashboardInternal",
      "deployKnowledgeService"
    ],
    limitations: []
  }
};

export const LIMITATION_WARNINGS = {};

export const ERROR_MESSAGES = {
  INVALID_RELEASE_NAME: "Release name can contain lowercase letters, digits, hyphens, and dots, and must start with a letter and end with a letter or digit.",
  RELEASE_NAME_TOO_LONG: `Release name must not exceed ${RELEASE_NAME_MAX_LENGTH} characters.`,
  RELEASE_NAME_EXISTS: "Release name already exists.",
  INVALID_SHA: "Enter a valid commit SHA (40 hex characters).",
  INVALID_CONFIG_ID: "Config ID must be a valid ObjectId (24 hex characters), 'active', or 'draft'.",
  INVALID_ASSISTANT_ID: "Assistant ID must be a valid UUID.",
  MEMORY_OUT_OF_RANGE: "Memory must be between 1 and 10.",
  REQUIRED_FIELD: "This field is required."
};

export const STEP_LABELS = {
  USE_CASE: "Use Case",
  CONFIGURATION: "Configuration",
  REVIEW: "Review & Deploy",
  DETAILS: "Details"
};

export const DEPLOYMENT_URL_FIELDS = [
  { id: "dashboardClientUrl", label: "Dashboard Client" },
  { id: "dashboardServerUrl", label: "Dashboard Server" },
  { id: "dashboardInternalUrl", label: "Dashboard Internal" },
  { id: "widgetServerUrl", label: "Widget Server" },
  { id: "widgetServerDraftUrl", label: "Widget Server (Draft)" },
  { id: "widgetServerPreviewUrl", label: "Widget Server (Preview)" },
  { id: "widgetClientUrl", label: "Widget Client" },
  { id: "assistantsUrl", label: "Assistants" },
  { id: "assistantsApiUrl", label: "Assistants API" },
  { id: "assistantsPreviewUrl", label: "Assistants Preview" },
  { id: "dataServiceUrl", label: "Data Service" },
  { id: "knowledgeServiceUrl", label: "Knowledge Service" }
];

export const WIZARD_INITIAL_DATA = {
  releaseName: "",
  dashboardClientTag: "",
  dashboardServerTag: "",
  configurationServiceTag: "",
  dataServiceTag: "",
  nluRuntimeTag: "",
  nluMode: "",
  widgetServerTag: "",
  voiceGatewayTag: "",
  useDraftConfiguration: false,
  deployDashboardInternal: false,
  deployKnowledgeService: false,
  splitDraftPreview: false,
  widgetClientTag: "",
  nluRuntimeModes: [],
  ttlHours: DEFAULT_TTL_HOURS,
  configId: "",
  assistantId: "",
  memory: DEFAULT_MEMORY
};

export const getServicesForUseCase = (useCaseId) => {
  const useCase = USE_CASES[useCaseId];
  if (!useCase) return { required: [], optional: [] };

  const required = [];
  const optional = [];

  Object.entries(useCase.services).forEach(([serviceKey, config]) => {
    const fieldDef = SERVICE_FIELDS[serviceKey];
    if (!fieldDef) return;

    const { requirement, showLatestToggle = true, hideExtraFields = [], extraFields: useCaseServiceExtraFields = [], modeSelector = false, subtitle } = config;
    const hideExtraFieldsSet = new Set(hideExtraFields);

    let resolvedExtraFields;
    if (modeSelector) {
      const dedicatedDef = SERVICE_FIELDS.nluRuntimeDedicated;
      resolvedExtraFields = (dedicatedDef.extraFields || [])
        .map((key) => EXTRA_FIELDS[key])
        .filter(Boolean);
    } else {
      // service-level extra fields are inherited by every use case; a use case can hide some or add its own
      resolvedExtraFields = [...(fieldDef.extraFields || []), ...useCaseServiceExtraFields]
        .filter((key) => !hideExtraFieldsSet.has(key))
        .map((key) => EXTRA_FIELDS[key])
        .filter(Boolean);
    }

    const entry = { ...fieldDef, serviceKey, extraFields: resolvedExtraFields, showLatestToggle, modeSelector, requirement, subtitle };
    if (requirement === SERVICE_REQUIREMENTS.REQUIRED) {
      required.push(entry);
    } else {
      optional.push(entry);
    }
  });

  return { required, optional };
};

export const getExtraFieldsForUseCase = (useCaseId) => {
  const useCase = USE_CASES[useCaseId];
  if (!useCase) return [];

  return useCase.extraFields.map(fieldKey => EXTRA_FIELDS[fieldKey]).filter(Boolean);
};

export const getActiveWarnings = (useCaseId) => {
  const useCase = USE_CASES[useCaseId];
  if (!useCase) return [];

  return useCase.limitations
    .map(key => LIMITATION_WARNINGS[key])
    .filter(Boolean);
};

export const PREFILL_TRIGGER_PARAM = "new";
export const PREFILL_TRIGGER_VALUE = "1";
export const PREFILL_USE_CASE_PARAM = "useCase";
export const PREFILL_EDIT_PARAM = "edit";

export const PREFILL_PARAM_KEYS = [
  PREFILL_TRIGGER_PARAM,
  PREFILL_USE_CASE_PARAM,
  PREFILL_EDIT_PARAM,
  ...Object.keys(WIZARD_INITIAL_DATA).filter((key) => key !== "nluRuntimeModes")
];
